import { downloadBlob, downloadText } from "./download";

const FORMULA_LIKE_TEXT = /^\s*[=+\-@]/;
const XLSX_MEDIA_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export function formulaSafeText(value) {
  if (typeof value === "string" && FORMULA_LIKE_TEXT.test(value)) {
    return `'${value}`;
  }
  return value;
}

export function csvCell(value) {
  const safeValue = formulaSafeText(value);
  const text = safeValue == null ? "" : String(safeValue);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function buildCsvText(rows) {
  const lines = (rows || []).map((row) =>
    (row || []).map(csvCell).join(",")
  );
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

export function downloadCsvRows(filename, rows) {
  downloadText(filename, buildCsvText(rows));
}

function normalizedExcelValue(value) {
  if (value == null) return "";
  return value;
}

function applyColumnWidths(worksheet, rows) {
  const columnCount = Math.max(0, ...(rows || []).map((row) => row?.length || 0));
  worksheet["!cols"] = Array.from({ length: columnCount }, (_, columnIndex) => {
    const longest = Math.max(
      0,
      ...(rows || []).map((row) =>
        String(row?.[columnIndex] == null ? "" : row[columnIndex]).length
      )
    );
    return { wch: Math.max(10, Math.min(48, longest + 2)) };
  });
}

export function createExcelWorkbook(XLSX, rows, options = {}) {
  const matrix = (rows || []).map((row) =>
    (row || []).map(normalizedExcelValue)
  );
  const worksheet = XLSX.utils.aoa_to_sheet(matrix);

  matrix.forEach((row, rowIndex) => {
    row.forEach((value, columnIndex) => {
      if (typeof value !== "string") return;
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      if (worksheet[address]) {
        worksheet[address].t = "s";
        delete worksheet[address].f;
      }
    });
  });

  applyColumnWidths(worksheet, matrix);
  if (matrix.length > 0 && matrix[0].length > 0) {
    const blankIndex = matrix.findIndex((row) => row.length === 0);
    const headerIndex = blankIndex >= 0 && blankIndex + 1 < matrix.length
      ? blankIndex + 1
      : 0;
    worksheet["!autofilter"] = {
      ref: XLSX.utils.encode_range({ r: headerIndex, c: 0 }, {
        r: Math.max(0, matrix.length - 1),
        c: Math.max(0, matrix[headerIndex].length - 1),
      }),
    };
  }

  const workbook = XLSX.utils.book_new();
  const requestedName = String(options.sheetName || "Export").slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, requestedName || "Export");
  return workbook;
}

export async function downloadExcelRows(filename, rows, options = {}) {
  const XLSX = await import("xlsx");
  const workbook = createExcelWorkbook(XLSX, rows, options);
  const content = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    compression: true,
  });
  downloadBlob(filename, new Blob([content], { type: XLSX_MEDIA_TYPE }));
}
